// src/components/ledger/AccountsPayable.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { 
  collection, getDocs, updateDoc, deleteDoc, doc, getDoc,
  query, orderBy, Timestamp, runTransaction 
} from 'firebase/firestore';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export const AccountsPayable = () => {
  const [payables, setPayables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPayable, setSelectedPayable] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);

  useEffect(() => {
    fetchPayables();
  }, []);

  const fetchPayables = async () => {
    setLoading(true);
    try {
      const payableRef = collection(db, 'accountsPayable');
      const snapshot = await getDocs(query(payableRef, orderBy('createdAt', 'desc')));
      const payableData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPayables(payableData);
    } catch (error) {
      console.error('Error fetching payables:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePayable = async (id, formData) => {
    try {
      if (!id) {
        alert('No payable ID provided');
        return;
      }
      
      const payableRef = doc(db, 'accountsPayable', id);
      
      const updateData = {
        type: formData.type,
        name: formData.name,
        phone: formData.phone || '',
        email: formData.email || '',
        description: formData.description || '',
        paymentType: formData.paymentType,
        updatedAt: Timestamp.now()
      };

      await updateDoc(payableRef, updateData);
      await fetchPayables();
      setEditingItem(null);
      alert('Account updated successfully!');
    } catch (error) {
      console.error('Error updating payable:', error);
      alert('Error updating account: ' + error.message);
    }
  };

  const handleDeletePayable = async (id) => {
    if (!window.confirm('Are you sure you want to delete this account?')) return;
    try {
      await deleteDoc(doc(db, 'accountsPayable', id));
      await fetchPayables();
      alert('Account deleted successfully!');
    } catch (error) {
      console.error('Error deleting payable:', error);
      alert('Error deleting account: ' + error.message);
    }
  };

  const handleRecordPayment = async () => {
    const amount = parseFloat(paymentAmount);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid amount');
      return;
    }
  
    if (amount > selectedPayable.currentBalance) {
      alert(`Amount exceeds balance due of ${selectedPayable.currentBalance.toLocaleString()} UGX`);
      return;
    }
  
    setPaymentLoading(true);
    try {
      await runTransaction(db, async (transaction) => {
        const payableRef = doc(db, 'accountsPayable', selectedPayable.id);
        const payableDoc = await transaction.get(payableRef);
        const current = payableDoc.data();
        
        const newBalance = current.currentBalance - amount;
        const newPaidAmount = (current.paidAmount || 0) + amount;
        const newStatus = newBalance === 0 ? 'paid' : 'partial';
        
        const updatedSalaryHistory = [...(current.salaryHistory || [])];
        let remainingAmount = amount;
        
        for (let i = 0; i < updatedSalaryHistory.length && remainingAmount > 0; i++) {
          if (updatedSalaryHistory[i].status === 'pending') {
            const salaryAmount = updatedSalaryHistory[i].amount;
            const paidOnThis = Math.min(remainingAmount, salaryAmount);
            
            updatedSalaryHistory[i] = {
              ...updatedSalaryHistory[i],
              status: paidOnThis === salaryAmount ? 'paid' : 'partial',
              paidAmount: (updatedSalaryHistory[i].paidAmount || 0) + paidOnThis,
              paidDate: Timestamp.now(),
              paymentMethod: paymentMethod,
              paymentReference: paymentReference
            };
            remainingAmount -= paidOnThis;
          }
        }
        
        const paymentRecord = {
          id: Date.now().toString(),
          amount: amount,
          date: Timestamp.now(),
          method: paymentMethod,
          reference: paymentReference || `Payment to ${current.name}`,
          previousBalance: current.currentBalance,
          newBalance: newBalance
        };
        
        const paymentHistory = [...(current.paymentHistory || []), paymentRecord];
        
        const transactionRecord = {
          id: Date.now().toString(),
          type: 'payment',
          amount: amount,
          date: Timestamp.now(),
          description: `Payment of ${amount.toLocaleString()} UGX via ${paymentMethod}`,
          previousBalance: current.currentBalance,
          newBalance: newBalance,
          reference: paymentReference,
          createdBy: 'system'
        };
        
        const transactionHistory = [...(current.transactionHistory || []), transactionRecord];
        
        transaction.update(payableRef, {
          currentBalance: newBalance,
          paidAmount: newPaidAmount,
          status: newStatus,
          salaryHistory: updatedSalaryHistory,
          paymentHistory: paymentHistory,
          transactionHistory: transactionHistory,
          updatedAt: Timestamp.now()
        });
        
        const journalRef = doc(collection(db, 'journalEntries'));
        transaction.set(journalRef, {
          journalId: journalRef.id,
          transactionId: payableRef.id,
          transactionType: 'payment_to_payable',
          date: Timestamp.now(),
          entries: [
            {
              account: current.type === 'employee' ? 'salaries_payable' : 'accounts_payable',
              accountName: current.type === 'employee' ? 'Salaries Payable' : 'Accounts Payable',
              debit: amount,
              credit: 0,
              description: `Payment to ${current.name} - ${current.type}`
            },
            {
              account: 'cash',
              accountName: 'Cash Account',
              debit: 0,
              credit: amount,
              description: `Payment to ${current.name}`
            }
          ],
          createdAt: Timestamp.now(),
          createdBy: 'system'
        });
      });
      
      await fetchPayables();
      setShowPaymentModal(false);
      setSelectedPayable(null);
      setPaymentAmount('');
      setPaymentReference('');
      alert('Payment recorded successfully!');
    } catch (error) {
      console.error('Error recording payment:', error);
      alert('Error recording payment: ' + error.message);
    } finally {
      setPaymentLoading(false);
    }
  };

  const getFilteredPayables = () => {
    let filtered = [...payables];
    if (filterType !== 'all') {
      filtered = filtered.filter(p => p.type === filterType);
    }
    return filtered;
  };

  const getPaginatedData = () => {
    const filtered = getFilteredPayables();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  const getTotalPages = () => Math.ceil(getFilteredPayables().length / itemsPerPage);

  const getTypeIcon = (type) => {
    switch(type) {
      case 'employee': return '👨‍💼';
      case 'subcontractor': return '👷';
      case 'supplier': return '🏭';
      default: return '📋';
    }
  };

  const generateReport = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const filteredPayables = getFilteredPayables();

    doc.setFillColor(127, 26, 46);
    doc.rect(0, 0, pageWidth, 40, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.text('ACCOUNTS PAYABLE REPORT', pageWidth / 2, 25, { align: 'center' });
    
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 20, 55);
    
    const tableData = filteredPayables.map(p => [
      getTypeIcon(p.type),
      p.name,
      p.paymentType === 'monthly' ? 'Monthly Salary' : 'One-time Payment',
      p.description || '-',
      p.phone || '-',
      p.email || '-'
    ]);
    
    autoTable(doc, {
      startY: 70,
      head: [['', 'Name', 'Payment Type', 'Description', 'Phone', 'Email']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [127, 26, 46], textColor: [255, 255, 255] },
      styles: { fontSize: 9 }
    });
    
    doc.save(`Accounts_List_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const paginatedData = getPaginatedData();
  const totalPages = getTotalPages();

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>Loading accounts...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Accounts Payable</h1>
          <p style={styles.subtitle}>Manage payments to employees, subcontractors, and suppliers</p>
        </div>
        <div style={styles.headerActions}>
          <button style={styles.reportButton} onClick={generateReport}>
            📄 Export Report
          </button>
        </div>
      </div>

      <div style={styles.infoNote}>
        <span>ℹ️</span>
        <span>Add new accounts from the "All Accounts" tab. Here you can record payments and edit/delete existing accounts.</span>
      </div>

      <div style={styles.filtersBar}>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Account Type</label>
          <select 
            style={styles.filterSelect} 
            value={filterType} 
            onChange={(e) => { setFilterType(e.target.value); setCurrentPage(1); }}
          >
            <option value="all">All Types</option>
            <option value="employee">Employees</option>
            <option value="subcontractor">Subcontractors</option>
            <option value="supplier">Suppliers</option>
          </select>
        </div>
      </div>

      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.tableHeaderRow}>
              <th style={styles.tableHeader}>Type</th>
              <th style={styles.tableHeader}>Name</th>
              <th style={styles.tableHeader}>Payment Type</th>
              <th style={styles.tableHeader}>Phone</th>
              <th style={styles.tableHeader}>Email</th>
              <th style={styles.tableHeader}>Description</th>
              <th style={styles.tableHeader}>Amount Paid</th>
              <th style={styles.tableHeader}>Balance Due</th>
              <th style={styles.tableHeader}>Actions</th>
             </tr>
          </thead>
          <tbody>
            {paginatedData.map(payable => (
              <tr key={payable.id} style={styles.tableRow}>
                <td style={styles.tableCell}><span style={{ fontSize: '20px' }}>{getTypeIcon(payable.type)}</span></td>
                <td style={{...styles.tableCell, fontWeight: '600'}}>{payable.name}</td>
                <td style={styles.tableCell}>
                  {payable.paymentType === 'monthly' ? 
                    <span style={{ background: '#dbeafe', padding: '4px 8px', borderRadius: '12px', fontSize: '11px' }}>📅 Monthly</span> : 
                    <span style={{ background: '#d1fae5', padding: '4px 8px', borderRadius: '12px', fontSize: '11px' }}>💰 One-time</span>
                  }
                </td>
                <td style={styles.tableCell}>{payable.phone || '-'}</td>
                <td style={styles.tableCell}>{payable.email || '-'}</td>
                <td style={styles.tableCell}>{payable.description || '-'}</td>
                <td style={{...styles.tableCell, color: '#10b981', fontWeight: '600'}}>{(payable.paidAmount || 0).toLocaleString()} UGX</td>
                <td style={{...styles.tableCell, ...((payable.currentBalance || 0) > 0 ? styles.balanceDue : styles.balanceZero)}}>
                  {(payable.currentBalance || 0).toLocaleString()} UGX
                </td>
                <td style={styles.tableCell}>
                  <div style={styles.actionButtons}>
                    {(payable.currentBalance || 0) > 0 && (
                      <button style={styles.payButton} onClick={() => { setSelectedPayable(payable); setShowPaymentModal(true); }}>💰 Pay</button>
                    )}
                    <button style={styles.editButton} onClick={() => setEditingItem(payable)}>✏️ Edit</button>
                    <button style={styles.deleteButton} onClick={() => handleDeletePayable(payable.id)}>🗑️ Delete</button>
                  </div>
                </td>
              </tr>
            ))}
            {paginatedData.length === 0 && (
              <tr><td colSpan="9" style={styles.emptyState}>No accounts found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div style={styles.pagination}>
          <button style={{...styles.pageButton, ...(currentPage === 1 ? styles.pageButtonDisabled : {})}} onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1}>Previous</button>
          <span style={styles.pageInfo}>Page {currentPage} of {totalPages}</span>
          <button style={{...styles.pageButton, ...(currentPage === totalPages ? styles.pageButtonDisabled : {})}} onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages}>Next</button>
        </div>
      )}

      {/* Edit Modal */}
      {editingItem && (
        <PayableEditModal
          payable={editingItem}
          onClose={() => setEditingItem(null)}
          onUpdate={handleUpdatePayable}
        />
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedPayable && (
        <PaymentModal
          payable={selectedPayable}
          amount={paymentAmount}
          setAmount={setPaymentAmount}
          method={paymentMethod}
          setMethod={setPaymentMethod}
          reference={paymentReference}
          setReference={setPaymentReference}
          loading={paymentLoading}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedPayable(null);
            setPaymentAmount('');
            setPaymentReference('');
          }}
          onSubmit={handleRecordPayment}
        />
      )}
    </div>
  );
};

// Edit Modal (only for editing, no add)
const PayableEditModal = ({ payable, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    type: payable?.type || 'employee',
    name: payable?.name || '',
    phone: payable?.phone || '',
    email: payable?.email || '',
    description: payable?.description || '',
    paymentType: payable?.paymentType || (payable?.type === 'employee' ? 'monthly' : 'one_time'),
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert('Please enter a name');
      return;
    }
    setLoading(true);
    try {
      await onUpdate(payable.id, formData);
      onClose();
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.container} onClick={(e) => e.stopPropagation()}>
        <div style={modalStyles.header}>
          <h2 style={modalStyles.title}>Edit Account</h2>
          <button style={modalStyles.closeButton} onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit} style={modalStyles.form}>
          <div style={modalStyles.formGroup}>
            <label>Account Type *</label>
            <select style={modalStyles.select} value={formData.type} onChange={(e) => {
              const newType = e.target.value;
              setFormData({ ...formData, type: newType, paymentType: newType === 'employee' ? 'monthly' : 'one_time' });
            }} required>
              <option value="employee">👨‍💼 Employee (Monthly Salary)</option>
              <option value="subcontractor">👷 Subcontractor (One-time)</option>
              <option value="supplier">🏭 Supplier (One-time)</option>
            </select>
          </div>
          <div style={modalStyles.formGroup}>
            <label>Full Name *</label>
            <input type="text" style={modalStyles.input} value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
          </div>
          <div style={modalStyles.formRow}>
            <div style={{ flex: 1 }}>
              <label>Phone</label>
              <input type="tel" style={modalStyles.input} value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
            </div>
            <div style={{ flex: 1 }}>
              <label>Email</label>
              <input type="email" style={modalStyles.input} value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} />
            </div>
          </div>
          <div style={modalStyles.formGroup}>
            <label>Description / Notes</label>
            <textarea style={{...modalStyles.input, ...modalStyles.textarea}} value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows="2" />
          </div>
          <div style={modalStyles.actions}>
            <button type="button" style={modalStyles.buttonSecondary} onClick={onClose}>Cancel</button>
            <button type="submit" style={modalStyles.buttonPrimary} disabled={loading}>{loading ? 'Saving...' : 'Update'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Payment Modal (same as before)
const PaymentModal = ({ payable, amount, setAmount, method, setMethod, reference, setReference, loading, onClose, onSubmit }) => {
  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.container} onClick={(e) => e.stopPropagation()}>
        <div style={modalStyles.header}>
          <h2 style={modalStyles.title}>Record Payment</h2>
          <button style={modalStyles.closeButton} onClick={onClose}>×</button>
        </div>
        <div style={modalStyles.form}>
          <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '12px', marginBottom: '20px' }}>
            <div><span>Payable To:</span> <strong>{payable?.name}</strong></div>
            <div><span>Payment Type:</span> <strong>{payable?.paymentType === 'monthly' ? 'Monthly Salary' : 'One-time Payment'}</strong></div>
            <div><span>Current Balance:</span> <strong>{(payable?.currentBalance || 0).toLocaleString()} UGX</strong></div>
          </div>
          <div style={modalStyles.formGroup}>
            <label>Payment Amount (UGX) *</label>
            <input type="number" style={modalStyles.input} value={amount} onChange={(e) => setAmount(e.target.value)} step="1000" required />
          </div>
          <div style={modalStyles.formGroup}>
            <label>Payment Method</label>
            <select style={modalStyles.select} value={method} onChange={(e) => setMethod(e.target.value)}>
              <option value="cash">💵 Cash</option>
              <option value="bank">🏦 Bank Transfer</option>
              <option value="mobile">📱 Mobile Money</option>
              <option value="cheque">📝 Cheque</option>
            </select>
          </div>
          <div style={modalStyles.formGroup}>
            <label>Reference (Optional)</label>
            <input type="text" style={modalStyles.input} value={reference} onChange={(e) => setReference(e.target.value)} />
          </div>
          <div style={modalStyles.actions}>
            <button type="button" style={modalStyles.buttonSecondary} onClick={onClose}>Cancel</button>
            <button type="button" style={modalStyles.buttonPrimary} onClick={onSubmit} disabled={loading || !amount || parseFloat(amount) <= 0}>{loading ? 'Processing...' : 'Record Payment'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Styles (keep as before, include balanceDue, balanceZero, etc.)
const styles = {
  container: { padding: '28px', background: '#f8fafc', minHeight: '100vh' },
  loadingContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px', color: '#64748b' },
  spinner: { width: '40px', height: '40px', border: '3px solid #e2e8f0', borderTopColor: '#7f1a2e', borderRadius: '50%', animation: 'spin 1s linear infinite', marginBottom: '16px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px', flexWrap: 'wrap', gap: '16px' },
  title: { fontSize: '24px', fontWeight: '700', color: '#0f172a', marginBottom: '4px' },
  subtitle: { fontSize: '13px', color: '#64748b' },
  headerActions: { display: 'flex', gap: '12px' },
  reportButton: { background: '#f1f5f9', color: '#1e293b', padding: '10px 20px', border: 'none', borderRadius: '10px', cursor: 'pointer' },
  infoNote: { display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 16px', background: '#eff6ff', borderRadius: '10px', marginBottom: '20px', fontSize: '12px', color: '#1e40af' },
  filtersBar: { display: 'flex', gap: '20px', marginBottom: '24px' },
  filterGroup: { display: 'flex', alignItems: 'center', gap: '12px' },
  filterLabel: { fontSize: '13px', fontWeight: '500', color: '#475569' },
  filterSelect: { padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '13px', background: '#ffffff', cursor: 'pointer' },
  tableWrapper: { overflowX: 'auto', borderRadius: '12px', border: '1px solid #eef2f6', background: '#ffffff' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  tableHeaderRow: { background: '#f8fafc', borderBottom: '1px solid #eef2f6' },
  tableHeader: { textAlign: 'left', padding: '14px 16px', fontWeight: '600', color: '#475569' },
  tableRow: { borderBottom: '1px solid #f1f5f9' },
  tableCell: { padding: '14px 16px', color: '#334155' },
  balanceDue: { color: '#f59e0b', fontWeight: '600' },
  balanceZero: { color: '#10b981', fontWeight: '600' },
  actionButtons: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  editButton: { background: '#3b82f6', color: '#fff', padding: '6px 12px', border: 'none', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' },
  deleteButton: { background: '#ef4444', color: '#fff', padding: '6px 12px', border: 'none', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' },
  payButton: { background: '#10b981', color: '#fff', padding: '6px 12px', border: 'none', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' },
  emptyState: { textAlign: 'center', padding: '48px', color: '#94a3b8' },
  pagination: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', marginTop: '24px', padding: '16px' },
  pageButton: { padding: '8px 16px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer' },
  pageButtonDisabled: { opacity: 0.5, cursor: 'not-allowed' },
  pageInfo: { fontSize: '13px', color: '#64748b' }
};

const modalStyles = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  container: { background: '#fff', borderRadius: '24px', width: '90%', maxWidth: '550px', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #eef2f6' },
  title: { fontSize: '20px', fontWeight: '600', color: '#0f172a' },
  closeButton: { background: 'none', border: 'none', fontSize: '28px', cursor: 'pointer', color: '#94a3b8' },
  form: { padding: '24px' },
  formGroup: { marginBottom: '20px' },
  formRow: { display: 'flex', gap: '16px', marginBottom: '20px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '500', color: '#334155', marginBottom: '8px' },
  input: { width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', outline: 'none' },
  textarea: { resize: 'vertical', fontFamily: 'inherit' },
  select: { width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', background: '#fff', cursor: 'pointer' },
  actions: { display: 'flex', gap: '12px', marginTop: '24px' },
  buttonPrimary: { flex: 1, background: '#7f1a2e', color: '#fff', padding: '12px', border: 'none', borderRadius: '10px', fontWeight: '500', cursor: 'pointer' },
  buttonSecondary: { flex: 1, background: '#f1f5f9', color: '#334155', padding: '12px', border: 'none', borderRadius: '10px', fontWeight: '500', cursor: 'pointer' }
};

const styleSheet = document.createElement("style");
styleSheet.textContent = `@keyframes spin { to { transform: rotate(360deg); } }`;
document.head.appendChild(styleSheet);

export default AccountsPayable;